import React, { useState } from 'react';
import { Lightbulb, Star, Clock, Loader2, X, RotateCcw, Trash2 } from 'lucide-react';
import ImportanceBadge from './ImportanceBadge';
import { timeAgo, parseMarkdownToHTML, getInitial, normalizeImportance } from '../utils/formatters';

export default function EmailCard({
  result,
  index,
  onStar,
  onSnooze,
  snoozeLabelId,
  onDismiss,
  sessionMeta,
  trashMode,
  onTrashRestore,
  onTrashDeleteForever,
}) {
  const [expanded, setExpanded] = useState(false);
  const [actionBusy, setActionBusy] = useState(null); // 'star' | 'snooze' | null
  const { email, importance, reason, summary, _sessionLabel } = result;
  const tier = normalizeImportance(importance);
  const labels = email.labels || [];
  const isStarred = labels.includes('STARRED');
  const isSnoozed = snoozeLabelId && labels.includes(snoozeLabelId);

  const runStar = async (e) => {
    e.stopPropagation();
    if (!onStar || actionBusy) return;
    setActionBusy('star');
    try {
      await onStar(email.id, !isStarred);
    } finally {
      setActionBusy(null);
    }
  };

  const runSnooze = async (e) => {
    e.stopPropagation();
    if (!onSnooze || actionBusy) return;
    setActionBusy('snooze');
    try {
      await onSnooze(email.id, !isSnoozed);
    } finally {
      setActionBusy(null);
    }
  };

  const handleDismissClick = (e) => {
    e.stopPropagation();
    if (!onDismiss || trashMode) return;
    const sessionId = result._sessionId ?? sessionMeta?.id;
    if (!sessionId) return;
    onDismiss({
      sessionId,
      sessionTitle: result._sessionLabel ?? sessionMeta?.title ?? '',
      emailId: email.id,
      result,
    });
  };

  return (
    <div
      className="email-card glass"
      data-tier={tier}
      data-email-id={email.id}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="email-card-header">
        <div className="email-sender-row">
          <div className="sender-avatar" data-tier={tier}>
            {getInitial(email.sender)}
          </div>
          <div className="email-sender-info">
            <div className="email-sender-name">{email.sender}</div>
            <div className="email-time">{timeAgo(email.date)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {trashMode ? (
            <>
              <button
                type="button"
                className="btn btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onTrashRestore?.();
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <RotateCcw size={14} /> Restore
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onTrashDeleteForever?.();
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Trash2 size={14} /> Delete
              </button>
              <ImportanceBadge tier={tier} />
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={runStar}
                disabled={!!actionBusy}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: actionBusy ? 'wait' : 'pointer',
                  color: isStarred ? 'var(--amber)' : 'var(--text-dim)',
                  padding: 4,
                  borderRadius: 4,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!actionBusy) e.currentTarget.style.color = 'var(--amber)';
                }}
                onMouseLeave={(e) => {
                  if (!isStarred) e.currentTarget.style.color = 'var(--text-dim)';
                }}
                title={isStarred ? 'Unstar in Gmail' : 'Star in Gmail'}
              >
                {actionBusy === 'star' ? (
                  <Loader2 size={16} className="spin" />
                ) : (
                  <Star size={16} fill={isStarred ? 'currentColor' : 'none'} />
                )}
              </button>
              <button
                type="button"
                onClick={runSnooze}
                disabled={!!actionBusy || !snoozeLabelId}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: actionBusy || !snoozeLabelId ? 'wait' : 'pointer',
                  color: isSnoozed ? 'var(--cyan)' : 'var(--text-dim)',
                  padding: 4,
                  borderRadius: 4,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!actionBusy && snoozeLabelId) e.currentTarget.style.color = 'var(--cyan)';
                }}
                onMouseLeave={(e) => {
                  if (!isSnoozed) e.currentTarget.style.color = 'var(--text-dim)';
                }}
                title={isSnoozed ? 'Return to Inbox' : 'Snooze (moves to InboxPulse/Snoozed)'}
              >
                {actionBusy === 'snooze' ? (
                  <Loader2 size={16} className="spin" />
                ) : (
                  <Clock size={16} style={{ opacity: snoozeLabelId ? 1 : 0.4 }} />
                )}
              </button>
              {onDismiss && (result._sessionId ?? sessionMeta?.id) && (
                <button
                  type="button"
                  onClick={handleDismissClick}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-dim)',
                    padding: 4,
                    borderRadius: 4,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--danger, #ff453a)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-dim)';
                  }}
                  title="Dismiss — move to trash (this app only)"
                >
                  <X size={16} />
                </button>
              )}
              <ImportanceBadge tier={tier} />
            </>
          )}
        </div>
      </div>

      <div className="email-subject">{email.subject}</div>
      {_sessionLabel && (
        <div
          style={{
            fontSize: '0.72rem',
            color: 'var(--text-dim)',
            marginTop: -4,
            marginBottom: 6,
          }}
        >
          Run: {_sessionLabel}
        </div>
      )}

      <div
        className="email-summary"
        dangerouslySetInnerHTML={{
          __html: parseMarkdownToHTML(
            expanded ? summary : summary?.slice(0, 200) + (summary?.length > 200 ? '...' : '')
          ),
        }}
      />

      {summary?.length > 200 && (
        <button type="button" className="email-expand-btn" onClick={() => setExpanded(!expanded)}>
          {expanded ? '▲ Show less' : '▼ Show more'}
        </button>
      )}

      <div className="email-reason" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <Lightbulb size={12} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>{reason}</span>
      </div>
    </div>
  );
}
